import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, setUnauthorizedHandler } from '../api/client.js';
import { authApi } from '../api/auth.js';

export type SessionState = 'checking' | 'signedOut' | 'signedIn';

interface SessionContextValue {
  state: SessionState;
  /** Non-null when the most recent login attempt failed. */
  loginError: string | null;
  rateLimitedUntil: number | null;
  login: (password: string, captchaId: string, captchaAnswer: string) => Promise<boolean>;
  logout: () => Promise<void>;
  /** Re-checks /auth/session — used after onboarding issues its own session. */
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>('checking');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);

  useEffect(() => {
    // Any 401 on an ordinary API call means the session is gone.
    setUnauthorizedHandler(() => setState('signedOut'));
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    authApi
      .session()
      .then((status) => {
        if (!cancelled) setState(status.authenticated ? 'signedIn' : 'signedOut');
      })
      .catch(() => {
        if (!cancelled) setState('signedOut');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (password: string, captchaId: string, captchaAnswer: string): Promise<boolean> => {
    setLoginError(null);
    try {
      await authApi.login(password, captchaId, captchaAnswer);
      setRateLimitedUntil(null);
      setState('signedIn');
      return true;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.isRateLimited) {
          const seconds = error.retryAfterSeconds ?? 900;
          setRateLimitedUntil(Date.now() + seconds * 1000);
          setLoginError('Too many attempts. Please wait before trying again.');
          return false;
        }
        if (error.isUnauthorized) {
          setLoginError(
            error.code === 'invalid_captcha'
              ? 'Captcha did not match — a new one has been generated.'
              : 'Incorrect password.',
          );
          return false;
        }
        setLoginError(error.message);
        return false;
      }
      setLoginError('Could not reach the server.');
      return false;
    }
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Even if the call fails, drop the local session state.
    }
    setState('signedOut');
  }, []);

  const refresh = useCallback(async () => {
    try {
      const status = await authApi.session();
      setState(status.authenticated ? 'signedIn' : 'signedOut');
    } catch {
      setState((previous) => (previous === 'signedIn' ? previous : 'signedOut'));
    }
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ state, loginError, rateLimitedUntil, login, logout, refresh }),
    [state, loginError, rateLimitedUntil, login, logout, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside <SessionProvider>');
  return context;
}
